import { Fragment, useId, useMemo, useState, useRef } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';
import ZoeMark from '../zoeAssistant/ZoeMark';

/** First row — familiar entity-style starters. */
const PRIMARY_SUGGESTED_PROMPTS = [
  "What needs my attention today?",
  'What happened to order WT303659?',
  'What happened to vehicle Y888WSM on 2026-05-11?',
  'Any driver issues for V88RNW on 2026-05-07?',
];

/** Second row — revealed via "More..." — fleet-manager focused. */
const MORE_SUGGESTED_PROMPTS = [
  'Which deliveries are at risk today?',
  'Where is telemetry missing or unreliable?',
  'What changed since yesterday that affects performance?',
];

/** Entity nouns we surface visually in chip text so fleet managers can scan
 *  the assistant's domain at a glance: order / vehicle / driver / deliveries.
 *  Matched case-insensitively on whole words; chips without a match render plain. */
const HIGHLIGHTED_CHIP_WORDS_RE = /\b(orders?|vehicles?|drivers?|deliveries|delivery)\b/gi;

export const PROFIT_PRIMARY_SUGGESTED_PROMPTS = [
  'What needs my attention this week on margins?',
  'Which customers had the lowest margin last month?',
  'Which vehicles are dragging fleet profitability?',
  'How did fuel and standing costs change vs last month?',
];

export const PROFIT_MORE_SUGGESTED_PROMPTS = [
  'Where should we raise rates on underperforming lanes?',
  "What's driving margin down compared to last month?",
];

export const PROFIT_HIGHLIGHTED_CHIP_WORDS_RE =
  /\b(margins?|profit(?:ability)?|revenue|pricing|customers?|vehicles?|routes?|fuel|costs?|standing)\b/gi;

export const PROFIT_AGENT_INPUT_PLACEHOLDER =
  'Ask about margins, customers, vehicles, or pricing…';

function renderHighlightedChipText(prompt: string, highlightRe: RegExp): React.ReactNode {
  const segments: React.ReactNode[] = [];
  let lastIdx = 0;
  for (const match of prompt.matchAll(highlightRe)) {
    const start = match.index ?? 0;
    if (start > lastIdx) {
      segments.push(prompt.slice(lastIdx, start));
    }
    segments.push(
      <span
        key={`hl-${start}`}
        className="text-warning font-semibold"
      >
        {match[0]}
      </span>,
    );
    lastIdx = start + match[0].length;
  }
  if (lastIdx < prompt.length) {
    segments.push(prompt.slice(lastIdx));
  }
  return segments.map((seg, i) => <Fragment key={i}>{seg}</Fragment>);
}

/** After the first successful investigation, chip row uses short "Try:" labelling only. */
export const ZOE_CHIP_INTRO_ONBOARDED_LS_KEY = 'zoe_onboarded_suggestions_v1';

export function isZoeChipIntroOnboarded(): boolean {
  try {
    return localStorage.getItem(ZOE_CHIP_INTRO_ONBOARDED_LS_KEY) === '1';
  } catch {
    return false;
  }
}

export function markZoeChipIntroOnboarded(): void {
  try {
    localStorage.setItem(ZOE_CHIP_INTRO_ONBOARDED_LS_KEY, '1');
  } catch {
    // ignore
  }
}

interface QueryBarProps {
  onSubmit: (question: string) => void;
  isLoading: boolean;
  /** True after first successful investigation (synced with localStorage). */
  zoeChipIntroOnboarded: boolean;
  placeholder?: string;
  suggestions?: string[];
  value?: string;
  onValueChange?: (value: string) => void;
  /** Override default Zoe chips (e.g. Profit Agent). */
  primarySuggestedPrompts?: string[];
  moreSuggestedPrompts?: string[];
  highlightPattern?: RegExp;
  submitLabel?: string;
  loadingLabel?: string;
  /** Smaller type and padding for narrow containers (the ZOE pop-up). */
  compact?: boolean;
  /**
   * Conversation starters under the input. On by default. Off for a follow-up
   * composer inside a thread: starters there ("What needs my attention
   * today?") read as a reset, and the answer card already offers follow-ups
   * that are about the conversation in progress.
   */
  showSuggestedPrompts?: boolean;
  /** `id` for the input, so a container can label or focus it. */
  inputId?: string;
  /** Accessible name for the input when no visible label points at it. */
  inputAriaLabel?: string;
  /** Focus ring and submit colour. `zoe` = ZOE purple (the pop-up). */
  accent?: 'green' | 'zoe';
}

export default function QueryBar({
  onSubmit,
  isLoading,
  zoeChipIntroOnboarded,
  placeholder,
  suggestions,
  value: controlledValue,
  onValueChange,
  primarySuggestedPrompts: primarySuggestedPromptsProp,
  moreSuggestedPrompts: moreSuggestedPromptsProp,
  highlightPattern,
  submitLabel = 'Investigate',
  loadingLabel = 'Investigating…',
  compact = false,
  showSuggestedPrompts = true,
  inputId,
  inputAriaLabel,
  accent = 'green',
}: QueryBarProps) {
  const [uncontrolledValue, setUncontrolledValue] = useState('');
  const [moreSuggestionsOpen, setMoreSuggestionsOpen] = useState(false);
  const value = controlledValue ?? uncontrolledValue;
  const setValue = onValueChange ?? setUncontrolledValue;
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const normalizedSuggestions = useMemo(() => {
    const raw = Array.isArray(suggestions) ? suggestions : [];
    const cleaned = raw
      .map((s) => String(s || '').trim())
      .filter(Boolean);
    // Dedupe while keeping order.
    return Array.from(new Set(cleaned)).slice(0, 25);
  }, [suggestions]);

  /** Native `<input list>` shows a dropdown arrow in Chromium; only wire it once the user is typing. */
  const showEntityDatalist = normalizedSuggestions.length > 0 && value.trim().length > 0;

  const primarySuggestedPrompts = primarySuggestedPromptsProp ?? PRIMARY_SUGGESTED_PROMPTS;
  const moreSuggestedPrompts = moreSuggestedPromptsProp ?? MORE_SUGGESTED_PROMPTS;
  const chipHighlightRe = highlightPattern ?? HIGHLIGHTED_CHIP_WORDS_RE;

  const handleSubmit = () => {
    const q = value.trim();
    if (!q || isLoading) return;
    onSubmit(q);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const handleSuggestion = (prompt: string) => {
    setValue(prompt);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      try {
        const len = prompt.length;
        inputRef.current?.setSelectionRange(len, len);
      } catch {
        // ignore if input type does not support selection
      }
    });
  };

  const handleClear = () => {
    setValue('');
    inputRef.current?.focus();
  };

  const chipClassName =
    'text-xs px-3 py-1 bg-transparent border border-border/70 rounded-full text-muted-foreground hover:text-success hover:border-success/50 transition-colors disabled:opacity-50';

  /** Stagger between consecutive chips in a row, in milliseconds. */
  const CHIP_STAGGER_MS = 40;

  const renderSuggestionChip = (prompt: string, animationDelayMs?: number) => (
    <button
      key={prompt}
      type="button"
      onClick={() => handleSuggestion(prompt)}
      disabled={isLoading}
      className={
        animationDelayMs !== undefined
          ? `${chipClassName} zoe-chip-fade-in`
          : chipClassName
      }
      style={
        animationDelayMs !== undefined
          ? { animationDelay: `${animationDelayMs}ms` }
          : undefined
      }
    >
      {renderHighlightedChipText(prompt, chipHighlightRe)}
    </button>
  );

  const suggestionRows = (opts: { showTryPrefix: boolean }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {opts.showTryPrefix ? (
          <span className={`${compact ? 'text-xs' : 'text-base'} font-semibold text-foreground shrink-0`}>Try:</span>
        ) : null}
        {primarySuggestedPrompts.map((p, i) =>
          renderSuggestionChip(p, i * CHIP_STAGGER_MS),
        )}
        <button
          type="button"
          onClick={() => setMoreSuggestionsOpen((o) => !o)}
          disabled={isLoading}
          aria-expanded={moreSuggestionsOpen}
          className="text-xs px-3 py-1 rounded-full border border-dashed border-accent text-muted-foreground hover:text-ring hover:border-ring/50 transition-colors disabled:opacity-50 shrink-0"
        >
          {moreSuggestionsOpen ? 'Less' : 'More...'}
        </button>
      </div>
      {moreSuggestionsOpen ? (
        <div
          key="more-suggestions"
          className="flex items-center gap-2 flex-wrap"
        >
          {moreSuggestedPrompts.map((p, i) =>
            renderSuggestionChip(p, i * CHIP_STAGGER_MS),
          )}
        </div>
      ) : null}
    </div>
  );

  const inputAccent =
    accent === 'zoe'
      ? 'focus:border-zoe-500 focus:ring-zoe-500'
      : 'focus:border-success focus:ring-success';
  const submitAccent = accent === 'zoe' ? 'bg-zoe-600 hover:bg-zoe-700' : 'bg-primary hover:bg-primary';

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className={`flex items-center ${compact ? 'gap-2' : 'gap-3'}`}>
        <div className="relative flex-1">
          {accent === 'zoe' ? (
            <ZoeMark className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zoe-300/70" />
          ) : (
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary/70" />
          )}
          {value ? (
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/40 transition-colors disabled:opacity-50"
              aria-label="Clear query"
              title="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          ) : null}
          <input
            ref={inputRef}
            id={inputId}
            aria-label={inputAriaLabel}
            type="text"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            placeholder={placeholder === undefined ? 'Ask about an order, vehicle, driver, or anomaly…' : placeholder}
            list={showEntityDatalist ? listId : undefined}
            className={`w-full pl-10 pr-10 ${compact ? 'py-2 text-sm' : 'py-3'} bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none ${inputAccent} focus:ring-1 disabled:opacity-50 disabled:cursor-not-allowed`}
          />
          {showEntityDatalist ? (
            <datalist id={listId}>
              {normalizedSuggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!value.trim() || isLoading}
          className={`${compact ? 'px-3 py-2 text-sm' : 'px-5 py-3'} ${submitAccent} disabled:bg-secondary disabled:text-muted-foreground text-white font-medium rounded-lg transition-colors flex items-center gap-2 shrink-0`}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {loadingLabel}
            </>
          ) : (
            submitLabel
          )}
        </button>
      </div>
      {!showSuggestedPrompts ? null : zoeChipIntroOnboarded ? (
        suggestionRows({ showTryPrefix: true })
      ) : (
        <div className="space-y-2">
          <p className={`${compact ? 'text-xs' : 'text-base'} text-muted-foreground leading-relaxed max-w-2xl`}>
            <span className="font-semibold text-foreground">New to Zoe?</span> Here are some questions to get started:
          </p>
          {suggestionRows({ showTryPrefix: false })}
        </div>
      )}
    </div>
  );
}
